import { useEffect, useMemo, useState } from "react";
import {
  X,
  Plus,
  Minus,
  Trash,
  ShoppingBag,
  WhatsappLogo,
  Printer,
  CheckCircle,
  Tag,
  CurrencyDollar,
} from "@phosphor-icons/react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";

const METODOS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "transferencia", label: "Transferencia" },
  { value: "otro", label: "Otro" },
];

/**
 * POSModal — counter-sale (mostrador) cart with multi-line items.
 * Workflow: edit cart -> "Cobrar" -> server creates ticket -> show printable digital ticket.
 */
export default function POSModal({ open, onClose, products, initialProductId, negocio, moneda = "MXN" }) {
  const [lines, setLines] = useState([]);
  const [comprador, setComprador] = useState({ nombre: "Mostrador", telefono: "", email: "" });
  const [metodoPago, setMetodoPago] = useState("efectivo");
  const [estado, setEstado] = useState("pagado");
  const [picker, setPicker] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(null);

  // Initialize cart with the product that triggered the modal
  useEffect(() => {
    if (!open) return;
    setTicket(null);
    setLines(initialProductId ? [{ product_id: initialProductId, cantidad: 1, precio_unitario: null }] : []);
    setComprador({ nombre: "Mostrador", telefono: "", email: "" });
    setMetodoPago("efectivo");
    setEstado("pagado");
    setPicker("");
    setBusy(false);
  }, [open, initialProductId]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const addLine = (productId) => {
    if (!productId) return;
    // If already in cart, just increment qty
    const exists = lines.findIndex((l) => l.product_id === productId);
    if (exists >= 0) {
      setLines((ls) =>
        ls.map((l, i) => (i === exists ? { ...l, cantidad: l.cantidad + 1 } : l)),
      );
    } else {
      setLines((ls) => [...ls, { product_id: productId, cantidad: 1, precio_unitario: null }]);
    }
    setPicker("");
  };

  const setQty = (idx, qty) => {
    const q = Math.max(1, Number(qty) || 1);
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, cantidad: q } : l)));
  };

  const setPrice = (idx, price) => {
    const v = price === "" ? null : Number(price);
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, precio_unitario: v } : l)));
  };

  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    let subtotal = 0;
    let unidades = 0;
    lines.forEach((l) => {
      const p = productMap[l.product_id];
      const pu = l.precio_unitario != null ? Number(l.precio_unitario) : Number(p?.precio_venta || 0);
      subtotal += pu * (l.cantidad || 1);
      unidades += l.cantidad || 1;
    });
    return { subtotal: round2(subtotal), total: round2(subtotal), unidades };
  }, [lines, productMap]);

  const submit = async () => {
    if (lines.length === 0) {
      toast.error("Agrega al menos un producto al ticket");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/sales/ticket", {
        comprador_nombre: comprador.nombre.trim() || "Mostrador",
        comprador_telefono: comprador.telefono.trim(),
        comprador_email: comprador.email.trim(),
        canal: "manual",
        metodo_pago: metodoPago,
        estado,
        lineas: lines.map((l) => ({
          product_id: l.product_id,
          cantidad: l.cantidad,
          precio_unitario: l.precio_unitario,
        })),
      });
      setTicket(data);
      toast.success(`Ticket ${data.folio} creado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el ticket");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="pos-modal"
    >
      <div
        className="bg-white border border-zinc-200 max-w-3xl w-full max-h-[95vh] overflow-auto print:max-h-none print:max-w-full print:border-0"
        onClick={(e) => e.stopPropagation()}
      >
        {ticket ? (
          <TicketView
            ticket={ticket}
            negocio={negocio}
            moneda={moneda}
            productMap={productMap}
            onClose={onClose}
            onNew={() => setTicket(null)}
          />
        ) : (
          <CartView
            lines={lines}
            productMap={productMap}
            products={products}
            picker={picker}
            setPicker={setPicker}
            addLine={addLine}
            setQty={setQty}
            setPrice={setPrice}
            removeLine={removeLine}
            comprador={comprador}
            setComprador={setComprador}
            metodoPago={metodoPago}
            setMetodoPago={setMetodoPago}
            estado={estado}
            setEstado={setEstado}
            totals={totals}
            moneda={moneda}
            busy={busy}
            submit={submit}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function CartView({
  lines,
  productMap,
  products,
  picker,
  setPicker,
  addLine,
  setQty,
  setPrice,
  removeLine,
  comprador,
  setComprador,
  metodoPago,
  setMetodoPago,
  estado,
  setEstado,
  totals,
  moneda,
  busy,
  submit,
  onClose,
}) {
  return (
    <div className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="overline text-klein flex items-center gap-1.5">
            <ShoppingBag size={12} weight="fill" /> Venta en mostrador
          </div>
          <h2 className="h-display text-2xl font-bold tracking-tight mt-1">Nuevo ticket</h2>
          <p className="text-xs text-muted2 mt-1">
            Agrega productos al carrito y genera un ticket digital imprimible o para enviar por WhatsApp.
          </p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-zinc-100" data-testid="pos-close-btn">
          <X size={18} />
        </button>
      </div>

      {/* Lines */}
      <div className="border border-zinc-200 mb-4" data-testid="pos-cart">
        <div className="hidden sm:grid grid-cols-12 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-[0.2em] font-medium text-muted2">
          <div className="col-span-5">Producto</div>
          <div className="col-span-2 text-right">Cant.</div>
          <div className="col-span-2 text-right">P. Unit.</div>
          <div className="col-span-2 text-right">Subtotal</div>
          <div className="col-span-1" />
        </div>
        {lines.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted2">
            <Tag size={24} className="mx-auto mb-2 text-zinc-400" />
            Carrito vacío. Agrega productos abajo.
          </div>
        ) : (
          lines.map((l, idx) => {
            const p = productMap[l.product_id];
            const pu = l.precio_unitario != null ? Number(l.precio_unitario) : Number(p?.precio_venta || 0);
            const sub = pu * (l.cantidad || 1);
            return (
              <div
                key={`${l.product_id || "_"}-${idx}`}
                className="px-3 py-2 border-b border-zinc-100 last:border-b-0 grid grid-cols-12 sm:items-center gap-2 text-sm"
                data-testid={`pos-line-${idx}`}
              >
                <div className="col-span-12 sm:col-span-5 min-w-0">
                  <div className="font-medium truncate">{p?.nombre || "Producto eliminado"}</div>
                  <div className="text-[11px] text-muted2 truncate">
                    {p?.categoria} · stock {p?.cantidad ?? "—"}
                  </div>
                </div>
                <div className="col-span-4 sm:col-span-2 sm:text-right">
                  <div className="inline-flex items-center border border-zinc-300">
                    <button
                      type="button"
                      onClick={() => setQty(idx, (l.cantidad || 1) - 1)}
                      className="px-2 py-1 hover:bg-zinc-50"
                      data-testid={`pos-line-${idx}-minus`}
                    >
                      <Minus size={12} />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={l.cantidad}
                      onChange={(e) => setQty(idx, e.target.value)}
                      className="w-12 text-center text-sm py-1 outline-none border-x border-zinc-200"
                      data-testid={`pos-line-${idx}-qty`}
                    />
                    <button
                      type="button"
                      onClick={() => setQty(idx, (l.cantidad || 1) + 1)}
                      className="px-2 py-1 hover:bg-zinc-50"
                      data-testid={`pos-line-${idx}-plus`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
                <div className="col-span-5 sm:col-span-2 sm:text-right">
                  <input
                    type="number"
                    step="0.01"
                    value={l.precio_unitario != null ? l.precio_unitario : pu}
                    onChange={(e) => setPrice(idx, e.target.value)}
                    className="input-flat text-sm py-1 sm:text-right tabular-nums"
                    data-testid={`pos-line-${idx}-price`}
                  />
                </div>
                <div className="col-span-2 sm:col-span-2 sm:text-right tabular-nums font-medium">{fmt(sub, moneda)}</div>
                <div className="col-span-1 sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
                    data-testid={`pos-line-${idx}-remove`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Quick add */}
      <div className="mb-5">
        <label className="overline block mb-1.5">Agregar otro producto</label>
        <div className="flex gap-2">
          <select
            value={picker}
            onChange={(e) => setPicker(e.target.value)}
            className="input-flat flex-1"
            data-testid="pos-picker"
            data-help="Elige un producto del inventario para sumarlo al ticket. Puedes repetir varias veces para múltiples artículos."
          >
            <option value="">— Selecciona —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.nombre} — ${fmt(p.precio_venta, moneda)} · stock ${p.cantidad}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => addLine(picker)}
            disabled={!picker}
            className="btn-klein disabled:opacity-50"
            data-testid="pos-add-line"
          >
            <Plus size={16} weight="bold" /> Agregar
          </button>
        </div>
      </div>

      {/* Customer & payment */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <div>
          <label className="overline block mb-1">Cliente (opcional)</label>
          <input
            type="text"
            value={comprador.nombre}
            onChange={(e) => setComprador({ ...comprador, nombre: e.target.value })}
            className="input-flat"
            data-testid="pos-buyer-name"
          />
        </div>
        <div>
          <label className="overline block mb-1">WhatsApp (opcional)</label>
          <input
            type="text"
            placeholder="5215512345678"
            value={comprador.telefono}
            onChange={(e) => setComprador({ ...comprador, telefono: e.target.value.replace(/[^0-9+]/g, "") })}
            className="input-flat"
            data-testid="pos-buyer-phone"
            data-help="Si lo agregas, podrás enviar el ticket digital por WhatsApp con un click."
          />
        </div>
        <div>
          <label className="overline block mb-1">Método de pago</label>
          <select
            value={metodoPago}
            onChange={(e) => setMetodoPago(e.target.value)}
            className="input-flat"
            data-testid="pos-method"
          >
            {METODOS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-1">Estado</label>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            className="input-flat"
            data-testid="pos-state"
          >
            <option value="pagado">Pagado</option>
            <option value="pendiente">Pendiente</option>
          </select>
        </div>
      </div>

      {/* Totals */}
      <div className="border border-zinc-200 p-4 mb-4 flex items-center justify-between gap-3" data-testid="pos-totals">
        <div>
          <div className="overline">Total a cobrar</div>
          <div className="text-[11px] text-muted2 mt-0.5">
            {totals.unidades} unidad{totals.unidades === 1 ? "" : "es"} · {lines.length} línea{lines.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="h-display text-3xl font-black text-klein tabular-nums">{fmt(totals.total, moneda)}</div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="btn-ghost" data-testid="pos-cancel">
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || lines.length === 0}
          className="btn-klein disabled:opacity-50"
          data-testid="pos-submit"
        >
          <CurrencyDollar size={16} weight="bold" />
          {busy ? "Generando…" : "Cobrar y generar ticket"}
        </button>
      </div>
    </div>
  );
}

function TicketView({ ticket, negocio, moneda, productMap, onClose, onNew }) {
  const fechaStr = (() => {
    try {
      return new Date(ticket.fecha).toLocaleString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return ticket.fecha;
    }
  })();

  const waMsg = encodeURIComponent(
    [
      `🧾 *Ticket ${ticket.folio}* — ${negocio || "Tienda"}`,
      fechaStr,
      "",
      ...ticket.lineas.map(
        (l) => `• ${l.cantidad}× ${l.product_nombre} — ${fmt(l.total, moneda)}`,
      ),
      "",
      `*Total:* ${fmt(ticket.totales.total, moneda)}`,
      `*Pago:* ${ticket.metodo_pago}`,
      `*Estado:* ${ticket.estado}`,
      "",
      "Gracias por tu compra.",
    ].join("\n"),
  );
  const waLink = ticket.comprador_telefono
    ? `https://wa.me/${ticket.comprador_telefono}?text=${waMsg}`
    : null;

  const printTicket = () => {
    document.body.classList.add("printing-ticket");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-ticket"), 500);
  };

  return (
    <div className="p-5 sm:p-6 ticket-print">
      <div className="flex items-start justify-between gap-3 mb-5 no-print">
        <div>
          <div className="overline text-klein flex items-center gap-1.5">
            <CheckCircle size={12} weight="fill" /> Ticket generado
          </div>
          <h2 className="h-display text-2xl font-bold tracking-tight mt-1">Folio {ticket.folio}</h2>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-zinc-100" data-testid="ticket-close-btn">
          <X size={18} />
        </button>
      </div>

      {/* Printable ticket */}
      <div className="border-2 border-dashed border-zinc-300 p-5 sm:p-6 font-mono text-sm" data-testid="ticket-printable">
        <div className="text-center pb-3 border-b border-zinc-200">
          <div className="h-display text-xl font-black tracking-tight not-italic font-sans">{negocio || "Tienda"}</div>
          <div className="text-[11px] text-muted2 mt-0.5">Ticket digital · VendeIA</div>
        </div>
        <div className="grid grid-cols-2 gap-1 text-[12px] py-3 border-b border-zinc-200">
          <span className="text-muted2">Folio</span>
          <span className="text-right font-bold tabular-nums">{ticket.folio}</span>
          <span className="text-muted2">Fecha</span>
          <span className="text-right">{fechaStr}</span>
          <span className="text-muted2">Cliente</span>
          <span className="text-right">{ticket.comprador_nombre || "Mostrador"}</span>
          {ticket.comprador_telefono && (
            <>
              <span className="text-muted2">Teléfono</span>
              <span className="text-right">{ticket.comprador_telefono}</span>
            </>
          )}
          <span className="text-muted2">Pago</span>
          <span className="text-right uppercase">{ticket.metodo_pago}</span>
          <span className="text-muted2">Estado</span>
          <span
            className="text-right uppercase font-bold"
            style={{ color: ticket.estado === "pagado" ? "#1B5E20" : "#B45309" }}
          >
            {ticket.estado}
          </span>
        </div>

        <div className="py-3 border-b border-zinc-200">
          <div className="text-[11px] text-muted2 uppercase tracking-[0.18em] mb-2">Detalle</div>
          {ticket.lineas.map((l, i) => {
            const prod = productMap[l.product_id];
            return (
              <div key={`${l.product_id || "_"}-${i}`} className="flex justify-between gap-2 py-1 text-[12px]">
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    {l.cantidad}× {l.product_nombre || prod?.nombre || "—"}
                  </div>
                  <div className="text-muted2">@ {fmt(l.precio_unitario, moneda)} c/u</div>
                </div>
                <div className="font-bold tabular-nums">{fmt(l.total, moneda)}</div>
              </div>
            );
          })}
        </div>

        <div className="py-3 flex justify-between items-baseline">
          <span className="text-muted2 text-[11px] uppercase tracking-[0.2em]">Total</span>
          <span className="h-display font-sans not-italic text-3xl font-black text-klein tabular-nums">
            {fmt(ticket.totales.total, moneda)}
          </span>
        </div>

        <div className="text-center pt-3 border-t border-zinc-200 text-[10px] text-muted2 leading-tight">
          {ticket.totales.unidades} unidad{ticket.totales.unidades === 1 ? "" : "es"} · Gracias por tu compra
          <div className="mt-1 font-mono text-[9px] opacity-70">{ticket.ticket_id}</div>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-2 no-print">
        <button
          type="button"
          onClick={printTicket}
          className="btn-klein flex-1"
          data-testid="ticket-print-btn"
        >
          <Printer size={16} weight="bold" /> Imprimir
        </button>
        {waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1eb858] flex-1"
            data-testid="ticket-wa-btn"
          >
            <WhatsappLogo size={16} weight="fill" /> Enviar por WhatsApp
          </a>
        )}
        <button type="button" onClick={onNew} className="btn-ghost flex-1" data-testid="ticket-new-btn">
          Nuevo ticket
        </button>
      </div>

      {/* Print-only style: hides everything except the ticket */}
      <style>{`
        @media print {
          body.printing-ticket > *:not(.fixed) { display: none !important; }
          body.printing-ticket .fixed { background: white !important; padding: 0 !important; }
          body.printing-ticket .ticket-print { padding: 12px !important; }
          body.printing-ticket .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
